import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router';
import { Button, ErrorState, HeaderSlotContext, Spinner } from '@/components/ui';
import { useT } from '@/i18n';
import { api } from '@/lib/api/client';
import { type FeatureKey, type MeView, type SchoolProfile, WEB_ROLES } from '@/lib/api/types';
import { AppContext, type AppContextValue, useApp } from '@/lib/auth/app-context';
import { roleCan } from '@/lib/auth/permissions';
import { session, signOut } from '@/lib/auth/session';
import { useSession } from '@/lib/auth/useSession';
import { FeatureOffPage, ForbiddenPage, TeacherOffPage } from '../RouteError';
import type { RouteHandle } from '../router';
import { Banners } from './Banners';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const ALL_OFF = {} as Record<FeatureKey, boolean>;

export const meKey = (membershipId: string | null) => ['me', membershipId] as const;
export const schoolKey = ['school'] as const;

/**
 * Loads who is signed in (/auth/me) and the school (/school), then renders
 * the layout. Each school switch clears the query cache, so nothing from the
 * previous school can leak into the next.
 */
export function AppShell() {
  const t = useT();
  const s = useSession()!;
  const membershipId = s.currentMembershipId;
  const scoped = !!membershipId;

  const me = useQuery({ queryKey: meKey(membershipId), queryFn: () => api.get<MeView>('/auth/me'), staleTime: 5 * 60_000 });
  const role = me.data?.role ?? null;
  // Office roles and teachers (attendance) use the web; parents and students use the app.
  const web = !!role && WEB_ROLES.includes(role);
  const school = useQuery({
    queryKey: schoolKey,
    queryFn: () => api.get<SchoolProfile>('/school'),
    enabled: scoped && web,
    staleTime: 5 * 60_000,
  });

  // Keep the stored user and memberships fresh (names and roles can change between logins).
  useEffect(() => {
    if (!me.data) return;
    const user = me.data.user ? { ...s.user, ...me.data.user } : s.user;
    const changed = JSON.stringify(me.data.memberships) !== JSON.stringify(s.memberships) || JSON.stringify(user) !== JSON.stringify(s.user);
    if (changed) session.update({ memberships: me.data.memberships, user });
  }, [me.data, s.memberships, s.user]);

  const value = useMemo<AppContextValue | null>(() => {
    if (!me.data) return null;
    const features = me.data.features ? (Object.fromEntries(me.data.features.map((f) => [f.key, f.enabled])) as Record<FeatureKey, boolean>) : ALL_OFF;
    return {
      user: me.data.user ? { ...s.user, ...me.data.user } : s.user,
      isPlatformAdmin: me.data.isPlatformAdmin,
      memberships: me.data.memberships,
      membership: me.data.memberships.find((m) => m.id === membershipId) ?? null,
      role: me.data.role,
      school: school.data ?? null,
      features,
      featureList: me.data.features ?? [],
    };
  }, [me.data, school.data, s.user, membershipId]);

  if (!scoped && !s.user.isPlatformAdmin) return <Navigate to="/choose" replace />;

  if (me.isError || school.isError) {
    return (
      <FullPage>
        <ErrorState
          error={me.error ?? school.error}
          onRetry={() => {
            void me.refetch();
            if (scoped) void school.refetch();
          }}
        />
        <Button variant="link" onClick={() => void signOut()}>
          {t('shell.signOut')}
        </Button>
      </FullPage>
    );
  }
  if (!value || (scoped && web && !school.data)) {
    return (
      <FullPage>
        <Spinner label={t('app.loading')} />
      </FullPage>
    );
  }
  if (scoped && !web) return <Navigate to="/choose" replace />;

  return (
    <AppContext.Provider value={value}>
      <Layout />
    </AppContext.Provider>
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6">{children}</div>;
}

function Layout() {
  const t = useT();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const location = useLocation();
  // The mobile drawer is open for the page it was opened on; navigating closes it.
  const [navOpenOn, setNavOpenOn] = useState<string | null>(null);
  const navOpen = navOpenOn === location.pathname;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-ink px-4 py-3 text-white focus:not-sr-only focus:fixed focus:start-4 focus:top-4"
      >
        {t('nav.skip')}
      </a>
      <Sidebar open={navOpen} onClose={() => setNavOpenOn(null)} />
      <div className="flex min-w-0 flex-col">
        <Topbar onMenu={() => setNavOpenOn(location.pathname)} slotRef={setSlot} />
        <Banners />
        <main id="main" tabIndex={-1} className="flex min-w-0 flex-1 flex-col gap-5 px-4 pt-5 pb-12 outline-none sm:px-8 sm:pt-6">
          <HeaderSlotContext.Provider value={slot}>
            <RouteGate />
          </HeaderSlotContext.Provider>
        </main>
      </div>
    </div>
  );
}

/** Applies the route's rules: platform console, switched-off features and roles. */
function RouteGate() {
  const matches = useMatches();
  const handle = (matches[matches.length - 1]?.handle ?? {}) as RouteHandle;
  const ctx = useApp();
  const location = useLocation();

  if (!ctx.school) {
    // Unscoped platform admin: only the console exists.
    if (handle.platform) return <Outlet />;
    return <Navigate to="/platform" replace />;
  }
  if (handle.platform) return ctx.isPlatformAdmin ? <Navigate to="/choose" replace state={{ from: location.pathname }} /> : <ForbiddenPage />;
  // Teachers only have the attendance screens on the web.
  if (!roleCan(ctx.role, 'office') && !handle.teacher) {
    return ctx.features.studentAttendance ? <Navigate to="/attendance" replace /> : <TeacherOffPage />;
  }
  if (handle.feature && !ctx.features[handle.feature]) return <FeatureOffPage />;
  if (handle.perm && !roleCan(ctx.role, handle.perm)) return <ForbiddenPage />;
  return <Outlet />;
}

